import { isProductReadinessEnabled } from "@/utils/accountProductFlags";

/**
 * Helpers for the four single-select radio filter groups on the Employees list
 * (ER-10492 / MDP-551) — Primary Position (`occupies`), Profile Picture, Access,
 * Preparing For Position (`prepares`).
 *
 * Each group follows the Filter Modal Standard: "No Filter" (default) plus one
 * pair of options. Values are stable lowercase snake_case end-to-end:
 *
 *   - occupies:  "" (default) | occupies | does_not_occupy
 *   - picture:   "" (default) | has_picture | no_picture
 *   - access:    "" (default) | has_access | no_access
 *   - prepares:  "" (default) | prepares | does_not_prepare
 *
 * Access maps straight onto the pre-existing `has_access` query param used by the
 * "employees without access" banner. Occupies / profile_picture / prepares are the
 * boolean query params from ER-10491 on the Employees v2 API:
 *   `occupies` / `profile_picture` / `prepares` (true|false).
 *
 * The legacy RadioInput-based modals surface options by LABEL, so the helpers also
 * accept the human label strings ("Not Assigned to Primary", "No Access", …) so
 * persisted values survive whatever path produced them. Relabelling is display-only:
 * the stable values above must never change (they are persisted in localStorage).
 */

export const DEFAULT_EMPLOYEE_RADIO_FILTER_PAYLOAD = Object.freeze({
  occupies: "",
  profilePicture: "",
  access: "",
  prepares: "",
});

/** Group definition used by the FilterEmployeeModal and payload conversion. */
export const EMPLOYEE_RADIO_GROUPS = Object.freeze([
  {
    key: "occupies",
    label: "Primary Position",
    items: [
      { label: "No Filter", value: "" },
      { label: "Assigned to Primary", value: "occupies" },
      { label: "Not Assigned to Primary", value: "does_not_occupy" },
    ],
  },
  {
    key: "prepares",
    label: "Preparing For Position",
    items: [
      { label: "No Filter", value: "" },
      { label: "Assigned to Prepare for", value: "prepares" },
      { label: "Not Assigned to Prepare for", value: "does_not_prepare" },
    ],
  },
  {
    key: "access",
    label: "Access",
    items: [
      { label: "No Filter", value: "" },
      { label: "Has Access", value: "has_access" },
      { label: "No Access", value: "no_access" },
    ],
  },
  {
    key: "profilePicture",
    label: "Profile Picture",
    items: [
      { label: "No Filter", value: "" },
      { label: "Has Picture", value: "has_picture" },
      { label: "Does not have Picture", value: "no_picture" },
    ],
  },
]);

/** value slug -> display label. */
export const EMPLOYEE_RADIO_LABEL_BY_VALUE = Object.freeze(
  EMPLOYEE_RADIO_GROUPS.reduce((acc, group) => {
    group.items.forEach((item) => {
      if (item.value) acc[item.value] = item.label;
    });
    return acc;
  }, {})
);

/** display label -> value slug. */
const VALUE_BY_LABEL = Object.freeze(
  Object.entries(EMPLOYEE_RADIO_LABEL_BY_VALUE).reduce((acc, [value, label]) => {
    acc[label] = value;
    return acc;
  }, {})
);

/** lowercased display label -> value slug (tolerates "no access" style tokens). */
const VALUE_BY_LOWER_LABEL = Object.freeze(
  Object.entries(VALUE_BY_LABEL).reduce((acc, [label, value]) => {
    acc[label.toLowerCase()] = value;
    return acc;
  }, {})
);

/**
 * Normalize one applied-filter token to a stable slug.
 * Accepts the snake_case slug and the human label (any case); anything else yields "".
 */
export function normalizeEmployeeRadioFilterValue(value) {
  const token = String(value || "").trim();
  if (!token) return "";
  if (EMPLOYEE_RADIO_LABEL_BY_VALUE[token]) return token;
  return VALUE_BY_LABEL[token] || VALUE_BY_LOWER_LABEL[token.toLowerCase()] || "";
}

/** Applied-filter array -> { occupies, profilePicture, access, prepares } slugs. */
export function buildEmployeeRadioFilterPayload(filters = []) {
  const values = Array.isArray(filters) ? filters : [];
  const payload = { ...DEFAULT_EMPLOYEE_RADIO_FILTER_PAYLOAD };

  EMPLOYEE_RADIO_GROUPS.forEach((group) => {
    if (group.key === "prepares" && !isProductReadinessEnabled()) return;
    const item = group.items.find((option) => {
      if (!option.value) return false;
      return values.some((value) => normalizeEmployeeRadioFilterValue(value) === option.value);
    });
    if (item) payload[group.key] = item.value;
  });

  return payload;
}

/** Payload -> applied-filter array (only non-default values). */
export function payloadToEmployeeRadioFilterValues(payload = {}) {
  const values = [];
  EMPLOYEE_RADIO_GROUPS.forEach((group) => {
    if (group.key === "prepares" && !isProductReadinessEnabled()) return;
    const value = payload?.[group.key];
    if (value) values.push(value);
  });
  return values;
}

/**
 * Applied-filter array (slugs or labels) -> Employee v2 list API query params.
 * Only the keys that have an active selection are returned.
 */
export function employeeRadioFiltersToApi(filters = []) {
  const values = (Array.isArray(filters) ? filters : []).map(normalizeEmployeeRadioFilterValue);
  const api = {};

  if (values.includes("occupies")) api.occupies = true;
  else if (values.includes("does_not_occupy")) api.occupies = false;

  if (values.includes("has_picture")) api.profile_picture = true;
  else if (values.includes("no_picture")) api.profile_picture = false;

  if (values.includes("has_access")) api.has_access = true;
  else if (values.includes("no_access")) api.has_access = false;

  if (isProductReadinessEnabled()) {
    if (values.includes("prepares")) api.prepares = true;
    else if (values.includes("does_not_prepare")) api.prepares = false;
  }

  return api;
}

/** Stable slug -> display label ("" when the token is not one of the new groups). */
export function getEmployeeRadioFilterLabel(value) {
  const slug = normalizeEmployeeRadioFilterValue(value);
  return EMPLOYEE_RADIO_LABEL_BY_VALUE[slug] || "";
}

/** Display label -> stable slug ("" when unknown). */
export function getEmployeeRadioFilterValueFromLabel(label) {
  return normalizeEmployeeRadioFilterValue(label);
}
